import { ReactNode } from 'react'
import { Group, iconSizes, Stack, Table, Tooltip } from '@common/ui'
import { IconCheck } from '@common/ui/icons'
import { com } from '@eidu/web'
import ActionIconIndicator from './ActionIconIndicator'
import {
  CRUD_ACTIONS,
  ColumnHeader,
  RowHeader,
  findEffectiveAccess,
  findEffectiveGlobalCheck,
  findEffectiveGlobalPolicyNames,
  findEffectivePolicyNames,
  formatPermissionTarget,
} from './utils'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityId = com.eidu.entity.EntityId
import EntityWithLabelAndRelated from '../../../domain/entity/EntityWithLabelAndRelated'
import EntityAction = com.eidu.entity.permission.EntityAction
import Permission = com.eidu.entity.permission.Permission

type VisualizerTableCellProps = {
  row: Extract<RowHeader, { type: 'global' | 'entity' }>
  column: ColumnHeader
  columnByKey: ReadonlyMap<string, ColumnHeader>
  entityTypes: readonly EntityType[]
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
}

const buildTooltip = (
  permissions: readonly Permission[] | undefined,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>,
  policyNames: readonly string[]
): ReactNode => {
  const labels =
    permissions && permissions.length > 0
      ? Array.from(
          new Set(
            permissions.map((p) => formatPermissionTarget(p, entityTypes, entitiesByTypeId)).filter((s) => s.length > 0)
          )
        )
      : []
  if (labels.length === 0 && policyNames.length === 0) return null
  const policyLine =
    policyNames.length > 0 ? `Granted by polic${policyNames.length > 1 ? 'ies' : 'y'}: ${policyNames.join(', ')}` : null
  return (
    <Stack gap="xxs">
      {labels.map((label) => (
        <div key={label} style={{ whiteSpace: 'pre-wrap' }}>
          {label}
        </div>
      ))}
      {policyLine ? <div style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{policyLine}</div> : null}
    </Stack>
  )
}

const VisualizerTableCell = ({ row, column, columnByKey, entityTypes, entitiesByTypeId }: VisualizerTableCellProps) => (
  <Table.Td style={{ borderRight: '1px solid var(--trellis-color-outline)', textAlign: 'center' }}>
    {row.type === 'global' ? (
      row.action instanceof EntityAction ? (
        (() => {
          const result = findEffectiveAccess(columnByKey, column, row.action)
          const policyNames = findEffectivePolicyNames(columnByKey, column, row.action)
          const tooltip = buildTooltip(
            result?.sourceColumn.permissionsByAction.get(row.action),
            entityTypes,
            entitiesByTypeId,
            policyNames
          )
          return (
            <ActionIconIndicator
              Icon={IconCheck}
              access={result?.access}
              muted={result?.inherited}
              tooltip={tooltip}
              policyNames={policyNames}
            />
          )
        })()
      ) : (
        (() => {
          const result = findEffectiveGlobalCheck(columnByKey, column, row.action)
          const policyNames = findEffectiveGlobalPolicyNames(columnByKey, column, row.action)
          if (!result && policyNames.length === 0) return null
          const tooltip =
            policyNames.length > 0
              ? `Granted by polic${policyNames.length > 1 ? 'ies' : 'y'}: ${policyNames.join(', ')}`
              : undefined
          const icon = (
            <span style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
              <IconCheck
                size={iconSizes.sm}
                stroke={1.5}
                style={{
                  color: result?.inherited ? 'var(--trellis-color-text-disabled)' : 'var(--trellis-color-text-primary)',
                }}
              />
              {policyNames.length > 0 ? (
                <span
                  style={{
                    position: 'absolute',
                    left: -3,
                    top: -2,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: 'var(--trellis-color-accent)',
                  }}
                />
              ) : null}
            </span>
          )
          return tooltip ? (
            <Tooltip label={tooltip} withArrow position="top">
              {icon}
            </Tooltip>
          ) : (
            icon
          )
        })()
      )
    ) : row.type === 'entity' ? (
      <Group gap="xxs" justify="center" wrap="nowrap">
        {CRUD_ACTIONS.map(({ action, Icon }) => {
          const result = findEffectiveAccess(columnByKey, column, action, row.entityTypeId.toString())
          const policyNames = findEffectivePolicyNames(columnByKey, column, action, row.entityTypeId.toString())
          const tooltip = buildTooltip(
            result?.sourceColumn.permissionsByTypeAndAction.get(row.entityTypeId.toString())?.get(action),
            entityTypes,
            entitiesByTypeId,
            policyNames
          )
          return (
            <ActionIconIndicator
              key={action.toString()}
              Icon={Icon}
              access={result?.access}
              muted={result?.inherited}
              tooltip={tooltip}
              policyNames={policyNames}
            />
          )
        })}
      </Group>
    ) : null}
  </Table.Td>
)

export default VisualizerTableCell
